/**
 * TikZ figures, step 1 (design-handoffs/2026-10-07-tikz-figures).
 *
 * A figure block holds an image (today's figures, `kind` absent or "image") or
 * TikZ source (`kind: "tikz"`, `source` verbatim). Everything else a figure has
 * (caption, label, placement, width) stays as it is. A TikZ figure without a
 * caption is a bare picture: imported from a `tikzpicture` outside any figure,
 * shown inline, with no caption and no number, and printed without a float.
 *
 * The server compiles the source and serves the drawing:
 *   GET /api/documents/{doc}/blocks/{block}/figure.svg          → image/svg+xml
 *   … on a compile error                                        → 422 FigureError
 *   GET …/figure.svg?lastGood=true                              → the last SVG that drew (404 if none)
 */

export type FigureKind = "image" | "tikz";

/**
 * ⌥⏎ on a selected TikZ figure shows its source; again, the split view.
 * Which view shows is the node view's own state, so the editor's shortcut
 * tells it through this event on the editor's DOM, with the figure's position.
 */
export const TIKZ_TOGGLE_SOURCE_EVENT = "lilia:figure-tikz-toggle-source";
export interface TikzToggleDetail {
  pos: number;
}

/**
 * Insert → Figure → TikZ opens the new figure's split view on the template
 * gallery. The insert goes through the editor's insert event, so the request
 * waits here for the new (empty) TikZ figure to mount and take it.
 */
let splitRequest: { at: number; source: string } | null = null;
const SPLIT_REQUEST_TTL_MS = 3000;
/** `source`: what the new figure holds (Ask Lilia's "Open in split view" inserts one with source). */
export function requestTikzSplitOnInsert(source = ""): void {
  splitRequest = { at: Date.now(), source };
}
/** True, once, for the figure the request was for (a figure with other source leaves it waiting). */
export function takeTikzSplitRequest(source = ""): boolean {
  const r = splitRequest;
  if (!r) return false;
  if (Date.now() - r.at > SPLIT_REQUEST_TTL_MS) {
    splitRequest = null;
    return false;
  }
  if (r.source !== source) return false;
  splitRequest = null;
  return true;
}

/** What the canvas knows about a figure, whatever holds it (a node, a block). */
export interface FigureFields {
  kind?: unknown;
  source?: unknown;
  caption?: unknown;
  label?: unknown;
  placement?: unknown;
}

export function isTikz(c: FigureFields | null | undefined): boolean {
  return c?.kind === "tikz";
}

/** A TikZ figure with no caption: shown inline, unnumbered, printed bare. */
export function isBarePicture(c: FigureFields | null | undefined): boolean {
  return isTikz(c) && !String(c?.caption ?? "").trim();
}

/**
 * The label as `\label{}` prints it, mirroring the server's LabelKey.Effective:
 * a key with a colon is kept, a bare one gets the figure prefix.
 */
function effectiveLabel(label: string): string {
  const l = label.trim();
  if (!l) return "";
  return l.includes(":") ? l : `fig:${l}`;
}

/**
 * The LaTeX for a TikZ figure, as the server exports it: the source byte for
 * byte, wrapped in the figure environment; or the bare source when there is no
 * caption. `escapeCaption` is the caller's own caption escaping, so each
 * generator keeps its existing rule for the caption text.
 */
export function tikzFigureLatex(c: FigureFields, escapeCaption: (s: string) => string = (s) => s): string {
  const source = typeof c.source === "string" ? c.source : "";
  const caption = String(c.caption ?? "");
  if (!caption.trim()) return source;
  const placement = String(c.placement ?? "").trim() || "H";
  const label = effectiveLabel(String(c.label ?? ""));
  const lines = [`\\begin{figure}[${placement}]`, "\\centering", source, `\\caption{${escapeCaption(caption)}}`];
  if (label) lines.push(`\\label{${label}}`);
  lines.push("\\end{figure}");
  return lines.join("\n");
}

// The render route ---------------------------------------------------------------

export type FigureErrorKind = "tex" | "timeout" | "budget" | "unavailable";

/** A figure that didn't draw. "unavailable" is the client's own: the route couldn't be reached. */
export interface FigureError {
  kind: FigureErrorKind;
  message: string;
  /** 1-based, in the figure's own source; null when TeX gave none. */
  line: number | null;
  /** The failing line's text. */
  excerpt: string | null;
  /** The bad part of the excerpt, 0-based, end exclusive; null = the whole line. */
  column: { start: number; end: number } | null;
  hasLastGood: boolean;
}

/** Errors that are not the author's: the wording says so, and offers Try again. */
export function isOurSide(kind: FigureErrorKind): boolean {
  return kind !== "tex";
}

/**
 * The TeX message as an author reads it: no "!" prompt, no "LaTeX Error:" /
 * "Package pgf Error:" prefix, none of TeX's help boilerplate, one line.
 */
export function cleanTexMessage(raw: string): string {
  let m = raw
    .replace(/See the .*? (manual|documentation) for explanation\.?/gi, "")
    .replace(/Type\s+H <return>\s+for immediate help\.?/gi, "")
    .replace(/(^|\s)l\.\d+\b.*$/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  m = m.replace(/^!\s*/, "");
  m = m.replace(/^(LaTeX|Package [\w-]+|Class [\w-]+) Error:\s*/i, "");
  m = m.replace(/\.{2,}$/, ".").trim();
  return m || "TeX stopped without saying why.";
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** A 422 body as the canvas uses it; anything malformed reads as a plain TeX error. */
export function parseFigureError(body: unknown): FigureError {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  const kind: FigureErrorKind = b.kind === "timeout" || b.kind === "budget" ? b.kind : "tex";
  const col = b.column && typeof b.column === "object" ? (b.column as Record<string, unknown>) : null;
  const start = num(col?.start);
  const end = num(col?.end);
  return {
    kind,
    message: cleanTexMessage(typeof b.message === "string" ? b.message : ""),
    line: num(b.line),
    excerpt: typeof b.excerpt === "string" ? b.excerpt : null,
    column: start !== null && end !== null && end > start ? { start, end } : null,
    hasLastGood: b.hasLastGood === true,
  };
}

/**
 * The excerpt split round the bad part: before, the underlined part, after.
 * The column when given (clamped to the line), else the whole line.
 */
export function splitExcerpt(excerpt: string, column: FigureError["column"]): [string, string, string] {
  if (!column) return ["", excerpt, ""];
  const start = Math.max(0, Math.min(column.start, excerpt.length));
  const end = Math.max(start, Math.min(column.end, excerpt.length));
  if (end === start) return ["", excerpt, ""];
  return [excerpt.slice(0, start), excerpt.slice(start, end), excerpt.slice(end)];
}

/** What Copy error puts on the clipboard. */
export function errorText(e: FigureError): string {
  const where = e.line !== null ? `Line ${e.line}` : "";
  const excerpt = e.excerpt ? `${where ? `${where}: ` : ""}${e.excerpt}` : where;
  return excerpt ? `${e.message}\n${excerpt}` : e.message;
}

/**
 * A short, stable key for a source (FNV-1a, 32 bit), so the drawing for a new
 * source never comes from the cache of an old one.
 */
export function sourceHash(source: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < source.length; i++) {
    h ^= source.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export function figureSvgUrl(documentId: string, blockId: string, opts: { v?: string; lastGood?: boolean } = {}): string {
  const q = new URLSearchParams();
  if (opts.lastGood) q.set("lastGood", "true");
  if (opts.v) q.set("v", opts.v);
  const qs = q.toString();
  return `/api/documents/${encodeURIComponent(documentId)}/blocks/${encodeURIComponent(blockId)}/figure.svg${qs ? `?${qs}` : ""}`;
}

/** SVG text as an <img> src. A data URL: nothing to revoke, and no script in it runs. */
export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Whether an image can have a transparent background: PNG, GIF, WebP, SVG (by
 * extension or data-URL type). A JPEG can't. In Dark these sit on the paper
 * panel, like a TikZ drawing, so TikZ isn't singled out.
 */
export function mayBeTransparent(src: string): boolean {
  const s = src.trim().toLowerCase();
  const data = /^data:image\/([a-z+.-]+)/.exec(s);
  if (data) return /^(png|gif|webp|svg\+xml|apng|avif)$/.test(data[1]);
  const path = s.split(/[?#]/)[0];
  return /\.(png|gif|webp|svg|apng|avif)$/.test(path);
}
